import type { ReactNode } from 'react';
import type { InputProps } from '../../../components/Input/Input';
import type { SlugRulesType } from '../../../form/ContentObjectForm/ContentObjectForm';
import type {
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
} from '../../../lib/flotiq-client/types/api-response';
import type { UseAppForm } from '../../form';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useMemo } from 'react';
import slug from 'slug';
import { twMerge } from 'tailwind-merge';

import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { FormConfigFieldEvent } from '../../../lib/flotiq-plugins/plugin-events/FormConfigFieldEvent';
import { FormFieldListenersEvent } from '../../../lib/flotiq-plugins/plugin-events/FormFieldListenersEvent';
import { FormRenderFieldEvent } from '../../../lib/flotiq-plugins/plugin-events/FormRenderFieldEvent';
import { EMPTY_OBJECT } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import ElementFromPlugin from '../../../components/ElementFromPlugin/ElementFromPlugin';
import OverrideSwitch from '../../../components/OverrideSwitch/OverrideSwitch';

import useMediaUpload from '../../../hooks/api/useMediaUpload';
import usePluginResults from '../../../hooks/usePluginResults';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';

import { useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';
import { useFormApi } from '../../useFormApi';

const findInputType = (props: ContentTypePropertyConfig) => {
  switch (props.inputType) {
    case 'dateTime':
      return props.showTime ? 'datetime-local' : 'date';
    case 'number':
      return 'number';
    case 'email':
      return 'email';
    default:
      return 'text';
  }
};

type FieldListenersResult = Record<string, (...args: any[]) => void>;

export interface CtoCustomFieldProps {
  /**
   * Field name
   */
  name: string;
  /**
   * Cto field properties
   */
  properties: ContentTypePropertyConfig;
  /**
   * Cto field schema
   */
  schema: ContentTypePropertySchema;
  /**
   * If field is required
   */
  isRequired?: boolean;
  /**
   * If form or field is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * Label to override from props
   */
  label?: ReactNode;
  /**
   * If field is rendered in Content Type form
   */
  isCtdForm?: boolean;
  /**
   * Help text to display below the field
   */
  helpText?: ReactNode;
  /**
   * Field listeners
   */
  listeners?: FieldListenersResult;
  /**
   * Form field additional CSS classes
   */
  additionalClasses?: string;
  /**
   * Field test id
   */
  testId?: string;
  /**
   * Slugged fields rules
   */
  sluggedFields?: SlugRulesType;
  /**
   * Is form in editing mode
   */
  isEditing?: boolean;
}

/**
 * @emits FlotiqPlugins."flotiq.form.field::render"
 * @emits FlotiqPlugins."flotiq.form.field::config"
 * @emits FlotiqPlugins."flotiq.form.field.listeners::add"
 */
const CtoCustomField = ({
  name,
  properties,
  schema,
  isRequired = false,
  disabled = false,
  readonly = false,
  label = '',
  isCtdForm = false,
  helpText,
  listeners,
  additionalClasses = '',
  testId = '',
  sluggedFields,
  ...props
}: CtoCustomFieldProps & Omit<InputProps, 'name' | 'onChange' | 'value'>) => {
  const {
    contentType,
    initialData,
    isEditing,
    overriddenFields,
    isPatchable,
    formUniqueKey,
  } = use(ContentObjectFormContext);
  const { onUpload: onMediaUpload } = useMediaUpload();

  const form = useFormContext() as unknown as UseAppForm;
  const formProps = useFormApi();

  const value = useStore(
    form.store,
    ({ values }) => (values as Record<string, unknown>)[name],
  );

  const fieldLabel = label || properties.label || name;
  const isOverridden = !isPatchable || overriddenFields?.[name];
  const hasSwitch = isPatchable && !name.includes('].');
  const isDisabled =
    (isEditing && properties.readonly) ||
    disabled ||
    (!isOverridden && hasSwitch);

  const options = useMemo(() => {
    if (!['select', 'radio'].includes(properties.inputType)) return null;
    return properties.useOptionsWithLabels
      ? properties.optionsWithLabels || []
      : (properties.options || []).map((option) => ({
          value: option,
          label: option,
        }));
  }, [
    properties.inputType,
    properties.options,
    properties.optionsWithLabels,
    properties.useOptionsWithLabels,
  ]);

  const handleSlugCreationOnBlur = useCallback(() => {
    const rule = sluggedFields?.sources?.[name];

    if (isEditing || !rule || !rule.active) {
      return;
    }
    const targetValue = form.getFieldValue(rule?.target);
    if (targetValue) {
      return;
    }

    const sourceValue = form.getFieldValue(rule.source) as string;
    const slugValue = slug(sourceValue);

    form.setFieldValue(rule.target, slugValue);
  }, [sluggedFields?.sources, name, isEditing, form]);

  const getFieldComponent = useCallback(
    (subField: any) => {
      switch (properties.inputType) {
        case 'object':
          return subField.ListField;
        case 'textarea':
          return subField.Textarea;
        case 'geo':
          return subField.GeoField;
        case 'block':
          return subField.BlockInput;
        case 'select':
          return subField.Dropdown;
        case 'radio':
          return subField.RadioGroup;
        case 'checkbox':
          return subField.Switch;
        case 'datasource':
          return subField.RelationField;
        case 'richtext':
          return subField.RichText;
        case 'textMarkdown':
          return subField.Markdown;
        case 'simpleList':
          return subField.OptionsField;
        default:
          return subField.Input;
      }
    },
    [properties.inputType],
  );

  const getFieldProps = useCallback(() => {
    switch (properties.inputType) {
      case 'text':
        return {
          type: findInputType(properties),
          ...(sluggedFields?.targets?.[name] && {
            slugRules: sluggedFields?.targets?.[name],
          }),
          ...(sluggedFields?.sources?.[name] && {
            handleSlugCreationOnBlur,
          }),
          ...(properties?.isPassword && {
            notSensitiveToPasswordManager: true,
            passwordInputIconAdditionalClasses: 'top-[36px]',
          }),
        };
      case 'number':
      case 'email':
      case 'dateTime': {
        return {
          type: findInputType(properties),
        };
      }
      case 'block': {
        return {
          key: formUniqueKey,
          toolsList: properties.blockEditorTypes,
          onMediaUpload,
          additionalClasses: 'max-w-full',
        };
      }
      case 'textMarkdown':
      case 'richtext': {
        return {
          key: formUniqueKey,
          onMediaUpload,
          additionalClasses: 'max-w-full',
        };
      }
      case 'select': {
        return {
          options,
          nullable: !isRequired,
          multiple: properties.isMultiple || properties.multiple,
        };
      }
      case 'radio': {
        return {
          options,
          horizontal: true,
        };
      }
      case 'checkbox': {
        return {
          theme: 'dark',
          size: 'base',
          side: 'right',
          labelUp: true,
          additionalClasses: 'max-w-2xl justify-items-start!',
        };
      }
      case 'object': {
        return {
          itemsProps: properties.items,
          itemsSchema:
            schema && 'items' in schema ? schema.items : EMPTY_OBJECT,
          isEditing: isEditing,
          isFormDisabled: isDisabled,
          additionalClasses: 'max-w-full',
          isCtdForm,
        };
      }
      case 'datasource': {
        return {
          validation: properties.validation,
          onMediaUpload,
        };
      }
      case 'textarea': {
        return {
          additionalClasses: 'max-w-full',
        };
      }
      default:
        return {};
    }
  }, [
    properties,
    sluggedFields?.targets,
    sluggedFields?.sources,
    name,
    handleSlugCreationOnBlur,
    formUniqueKey,
    onMediaUpload,
    options,
    isRequired,
    schema,
    isEditing,
    isDisabled,
    isCtdForm,
  ]);

  const errors = useFieldErrors(name);

  const eventParams = useMemo(
    () => ({
      name,
      value,
      form: formProps,
      required: isRequired,
      disabled: isDisabled,
      readonly,
      properties,
      schema,
      error: errors,
      onMediaUpload,
      contentType,
      initialData,
      isEditing,
      formUniqueKey,
    }),
    [
      name,
      value,
      formProps,
      isRequired,
      isDisabled,
      readonly,
      properties,
      schema,
      errors,
      onMediaUpload,
      contentType,
      initialData,
      isEditing,
      formUniqueKey,
    ],
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field::render"
   */
  const pluginRenders = usePluginResults(
    'flotiq.form.field::render',
    FormRenderFieldEvent,
    eventParams,
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field.listeners::add"
   */
  const fieldListeners = usePluginResults(
    'flotiq.form.field.listeners::add',
    FormFieldListenersEvent,
    eventParams,
  );

  const groupedListeners = useMemo(
    () =>
      [...(listeners ? [listeners] : []), ...fieldListeners].reduce(
        (allListeners, pluginListeners) => {
          Object.entries(pluginListeners).forEach(([type, listener]) => {
            if (!allListeners[type]) allListeners[type] = [];
            allListeners[type].push(listener);
          });
          return allListeners;
        },
        {} as Record<string, ((...args: any[]) => void)[]>,
      ),
    [fieldListeners, listeners],
  );

  const finalListeners = useMemo(
    () => ({
      ...['onChange', 'onBlur', 'onMount', 'onSubmit']
        .filter((type) => groupedListeners[type]?.length > 0)
        .reduce((allListeners, type) => {
          allListeners[type] = (...props) => {
            groupedListeners[type].forEach((listener: any) => {
              listener(...props);
            });
          };
          return allListeners;
        }, {} as FieldListenersResult),
    }),
    [groupedListeners],
  );

  /**
   * @emits FlotiqPlugins."flotiq.form.field::config"
   */
  const fieldProps = useMemo(() => {
    const uniqueFieldProps = getFieldProps();
    const fieldProps = {
      name,
      additionalElements: undefined,
      className: undefined,
      direction: undefined,
      ...uniqueFieldProps,
    };

    FlotiqPlugins.run(
      'flotiq.form.field::config',
      new FormConfigFieldEvent({
        ...eventParams,
        config: fieldProps,
      }),
    );
    return fieldProps;
  }, [getFieldProps, name, eventParams]);

  const {
    key,
    additionalElements,
    className: additionalFieldClasses,
    additionalClasses: additionalComponentClasses,
    ...fieldProperties
  } = useMemo(() => fieldProps, [fieldProps]);

  if (pluginRenders?.length > 0) {
    return <ElementFromPlugin results={pluginRenders} />;
  }

  const fieldRowClasses =
    isPatchable && hasSwitch
      ? 'grid grid-cols-[fit-content(100px)_1fr] gap-2 md:gap-10'
      : 'relative min-w-0 w-full';

  if (!isCtdForm && properties.hidden) return null;
  if (['object', 'datasource', 'simpleList'].indexOf(properties.inputType) > -1)
    return (
      <div className={fieldRowClasses}>
        {hasSwitch && (
          <OverrideSwitch
            name={fieldProperties.name}
            readonly={properties.readonly}
            checked={isOverridden}
            unique={properties.unique}
            testId={testId}
          />
        )}
        <div className="relative min-w-0">
          <form.AppField
            key={key}
            name={fieldProperties.name}
            mode="array"
            listeners={finalListeners}
          >
            {(subField: any) => {
              const FieldComponent = getFieldComponent(subField);
              return (
                <FieldComponent
                  key={key}
                  label={fieldLabel}
                  required={isRequired && (!isPatchable || isOverridden)}
                  helpText={helpText || properties.helpText}
                  disabled={isDisabled}
                  readonly={readonly}
                  additionalClasses={twMerge(
                    additionalClasses,
                    additionalFieldClasses,
                    additionalComponentClasses,
                  )}
                  {...fieldProperties}
                  {...getTestProps(testId, name, 'testId')}
                />
              );
            }}
          </form.AppField>
          {additionalElements && (
            <ElementFromPlugin results={additionalElements} />
          )}
        </div>
      </div>
    );

  return (
    <div className={fieldRowClasses}>
      {hasSwitch && (
        <OverrideSwitch
          name={fieldProperties.name}
          readonly={properties.readonly}
          checked={isOverridden}
          unique={properties.unique}
          testId={testId}
        />
      )}
      <div className="relative min-w-0">
        <form.AppField
          key={key}
          name={fieldProperties.name}
          listeners={finalListeners}
        >
          {(subField: any) => {
            const FieldComponent = getFieldComponent(subField);
            return (
              <FieldComponent
                key={key}
                label={fieldLabel}
                required={isRequired && isOverridden}
                disabled={isDisabled}
                readonly={readonly}
                helpText={helpText || properties.helpText}
                additionalClasses={twMerge(
                  additionalClasses,
                  additionalFieldClasses,
                  additionalComponentClasses,
                )}
                {...props}
                {...fieldProperties}
                {...getTestProps(testId, name, 'testId')}
              />
            );
          }}
        </form.AppField>
        {additionalElements && (
          <ElementFromPlugin results={additionalElements} />
        )}
      </div>
    </div>
  );
};

export default CtoCustomField;
